// src/pages/AdminGLBA.tsx

import React, { useEffect, useState } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import Navigation from '../components/Navigation';
import { GLBAPreferences } from '../types/glbaPreferences';
import { sendGLBAPrivacyNotice } from '../lib/email/emailService';
import { markAnnualNoticeDelivered, getGLBAPreferences } from '../lib/glba/glbaPreferencesApi';

interface UserWithGLBAPrefs {
  userId: string;
  email: string;
  displayName: string;
  preferences: GLBAPreferences | null;
  lastAnnualNotice?: Date;
  nextAnnualNoticeDue?: Date;
}

const AdminGLBA: React.FC = () => {
  const [users, setUsers] = useState<UserWithGLBAPrefs[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [filter, setFilter] = useState<'all' | 'opted-out-info' | 'opted-out-affiliate' | 'needs-annual-notice'>('all');
  const [sendingNotices, setSendingNotices] = useState<string[]>([]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      // Get all users
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(usersRef, orderBy('createdAt', 'desc')));
      
      const usersList: UserWithGLBAPrefs[] = [];
      
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Get GLBA preferences for this user
        let preferences: GLBAPreferences | null = null;
        try {
          preferences = await getGLBAPreferences(userId);
        } catch (error) {
          // User may not have preferences yet
          console.log(`No GLBA preferences for user ${userId}`);
        }
        
        usersList.push({
          userId,
          email: userData.email || 'No email',
          displayName: userData.displayName || (userData.firstName && userData.lastName 
            ? `${userData.firstName} ${userData.lastName}` 
            : 'No name'),
          preferences,
          lastAnnualNotice: preferences?.lastAnnualNoticeDeliveredAt 
            ? (preferences.lastAnnualNoticeDeliveredAt as any)?.toDate?.() || new Date(preferences.lastAnnualNoticeDeliveredAt as any)
            : undefined,
          nextAnnualNoticeDue: preferences?.nextAnnualNoticeDue
            ? (preferences.nextAnnualNoticeDue as any)?.toDate?.() || new Date(preferences.nextAnnualNoticeDue as any)
            : undefined,
        });
      }
      
      setUsers(usersList);
    } catch (error: any) {
      console.error('Error loading users:', error);
      setStatus({ type: 'error', message: 'Failed to load users. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const sendAnnualNotice = async (userId: string, email: string) => {
    setSendingNotices(prev => [...prev, userId]);
    try {
      await sendGLBAPrivacyNotice(email, 'annual');
      await markAnnualNoticeDelivered(userId);
      setStatus({ type: 'success', message: `Annual notice sent to ${email}` });
      // Reload users to update the display
      await loadUsers();
    } catch (error: any) {
      console.error('Error sending annual notice:', error);
      setStatus({ type: 'error', message: `Failed to send notice to ${email}: ${error.message}` });
    } finally {
      setSendingNotices(prev => prev.filter(id => id !== userId));
    }
  };

  const sendAnnualNoticesToAll = async () => {
    const usersNeedingNotices = filteredUsers.filter(user => {
      if (!user.nextAnnualNoticeDue) return true;
      return user.nextAnnualNoticeDue <= new Date();
    });

    if (usersNeedingNotices.length === 0) {
      setStatus({ type: 'error', message: 'No users need annual notices at this time.' });
      return;
    }

    if (!window.confirm(`Send annual notices to ${usersNeedingNotices.length} users?`)) {
      return;
    }

    setSendingNotices(usersNeedingNotices.map(u => u.userId));
    let successCount = 0;
    let errorCount = 0;

    for (const user of usersNeedingNotices) {
      try {
        await sendGLBAPrivacyNotice(user.email, 'annual');
        await markAnnualNoticeDelivered(user.userId);
        successCount++;
      } catch (error: any) {
        console.error(`Error sending notice to ${user.email}:`, error);
        errorCount++;
      }
    }

    setSendingNotices([]);
    setStatus({
      type: successCount > 0 ? 'success' : 'error',
      message: `Sent ${successCount} notices successfully. ${errorCount > 0 ? `${errorCount} failed.` : ''}`
    });
    await loadUsers();
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'all') return true;
    if (filter === 'opted-out-info') return user.preferences?.optOutInformationSharing === true;
    if (filter === 'opted-out-affiliate') return user.preferences?.optOutAffiliateMarketing === true;
    if (filter === 'needs-annual-notice') {
      if (!user.nextAnnualNoticeDue) return true;
      return user.nextAnnualNoticeDue <= new Date();
    }
    return true;
  });

  const stats = {
    total: users.length,
    optedOutInfo: users.filter(u => u.preferences?.optOutInformationSharing).length,
    optedOutAffiliate: users.filter(u => u.preferences?.optOutAffiliateMarketing).length,
    needsAnnualNotice: users.filter(u => {
      if (!u.nextAnnualNoticeDue) return true;
      return u.nextAnnualNoticeDue <= new Date();
    }).length,
    initialNoticeDelivered: users.filter(u => u.preferences?.initialNoticeDeliveredAt).length,
  };

  if (isLoading) {
    return (
      <div>
        <Navigation />
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '1rem' }}>GLBA Compliance Management</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Manage financial privacy notices and opt-out preferences for all users.
        </p>

        {status && (
          <div
            style={{
              padding: '1rem',
              marginBottom: '2rem',
              borderRadius: '4px',
              backgroundColor: status.type === 'success' ? '#d4edda' : '#f8d7da',
              border: `1px solid ${status.type === 'success' ? '#28a745' : '#dc3545'}`,
              color: status.type === 'success' ? '#155724' : '#721c24',
            }}
          >
            {status.message}
          </div>
        )}

        {/* Statistics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '1rem'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#007bff' }}>{stats.total}</div>
            <div style={{ color: '#666' }}>Total Users</div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '1rem'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>{stats.optedOutInfo}</div>
            <div style={{ color: '#666' }}>Opted Out (Info Sharing)</div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '1rem'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#ffc107' }}>{stats.optedOutAffiliate}</div>
            <div style={{ color: '#666' }}>Opted Out (Affiliate)</div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '1rem'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stats.needsAnnualNotice > 0 ? '#dc3545' : '#28a745' }}>
              {stats.needsAnnualNotice}
            </div>
            <div style={{ color: '#666' }}>Need Annual Notice</div>
          </div>
          <div style={{
            backgroundColor: '#fff',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '1rem'
          }}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>{stats.initialNoticeDelivered}</div>
            <div style={{ color: '#666' }}>Initial Notice Sent</div>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          marginBottom: '2rem',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={sendAnnualNoticesToAll}
            disabled={stats.needsAnnualNotice === 0 || sendingNotices.length > 0}
            style={{
              backgroundColor: stats.needsAnnualNotice > 0 ? '#007bff' : '#6c757d',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: stats.needsAnnualNotice > 0 && sendingNotices.length === 0 ? 'pointer' : 'not-allowed',
              fontWeight: 'bold'
            }}
          >
            {sendingNotices.length > 0 ? 'Sending...' : `Send Annual Notices to ${stats.needsAnnualNotice} Users`}
          </button>
          <button
            onClick={loadUsers}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ marginRight: '1rem', fontWeight: 'bold' }}>Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              fontSize: '1rem'
            }}
          >
            <option value="all">All Users</option>
            <option value="opted-out-info">Opted Out (Info Sharing)</option>
            <option value="opted-out-affiliate">Opted Out (Affiliate Marketing)</option>
            <option value="needs-annual-notice">Needs Annual Notice</option>
          </select>
        </div>

        {/* Users Table */}
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>User</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Opt-Out Status</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Initial Notice</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Last Annual Notice</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Next Due</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
                    No users found matching the filter.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const needsNotice = !user.nextAnnualNoticeDue || user.nextAnnualNoticeDue <= new Date();
                  return (
                    <tr key={user.userId} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px' }}>{user.displayName}</td>
                      <td style={{ padding: '12px' }}>{user.email}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {user.preferences?.optOutInformationSharing && (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              backgroundColor: '#ffc107',
                              color: '#000',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}>
                              Info Sharing
                            </span>
                          )}
                          {user.preferences?.optOutAffiliateMarketing && (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              backgroundColor: '#ffc107',
                              color: '#000',
                              borderRadius: '4px',
                              fontSize: '0.875rem'
                            }}>
                              Affiliate
                            </span>
                          )}
                          {!user.preferences?.optOutInformationSharing && !user.preferences?.optOutAffiliateMarketing && (
                            <span style={{ color: '#666', fontSize: '0.875rem' }}>None</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        {user.preferences?.initialNoticeDeliveredAt ? (
                          <span style={{ color: '#28a745', fontSize: '0.875rem' }}>✓ Sent</span>
                        ) : (
                          <span style={{ color: '#dc3545', fontSize: '0.875rem' }}>Not sent</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {user.lastAnnualNotice ? (
                          <span style={{ fontSize: '0.875rem' }}>
                            {user.lastAnnualNotice.toLocaleDateString()}
                          </span>
                        ) : (
                          <span style={{ color: '#666', fontSize: '0.875rem' }}>Never</span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        {user.nextAnnualNoticeDue ? (
                          <span style={{
                            fontSize: '0.875rem',
                            color: needsNotice ? '#dc3545' : '#666',
                            fontWeight: needsNotice ? 'bold' : 'normal'
                          }}>
                            {user.nextAnnualNoticeDue.toLocaleDateString()}
                            {needsNotice && ' ⚠️'}
                          </span>
                        ) : (
                          <span style={{ color: '#dc3545', fontSize: '0.875rem', fontWeight: 'bold' }}>
                            Overdue
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => sendAnnualNotice(user.userId, user.email)}
                          disabled={sendingNotices.includes(user.userId)}
                          style={{
                            backgroundColor: needsNotice ? '#007bff' : '#6c757d',
                            color: 'white',
                            padding: '6px 12px',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: sendingNotices.includes(user.userId) ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          {sendingNotices.includes(user.userId) ? 'Sending...' : 'Send Notice'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminGLBA;
